import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Row } from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Sidebar from './sidebar';
import Header from 'modules/header';
import RouteValidator from 'services/routeValidator/validator';
import TipsManager from 'shared/components/TipsManager';

// order of bootstrapTheme and react-widgets was changed because
// localhost:3000 was not having same styles order like after deploy
import 'react-widgets/dist/css/react-widgets.css';
import 'assets/styles/bootstrapTheme.css';
import 'assets/styles/common/day-picker.css';
import 'assets/styles/common/react-metismenu-standart.min.css';
import 'assets/styles/common/main.css';
import 'assets/styles/common/theme.css';
import 'assets/styles/common/buttons.css';
import 'assets/styles/common/dropdowns.css';
import 'assets/styles/common/modals.css';
import 'assets/styles/common/sidebar.css';
import 'assets/styles/scheduler/scheduler.css';
import 'assets/styles/scheduler/daypilot.css';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { InProductReview, SurveyPopup } from '@hub-mono/app-modules';
import { createReview, updateReview } from '../../../api/review';
import { sendContact } from '../../../api/general';
import { useSelector } from 'react-redux';
import { useHubSpotChat } from '../../../hooks';
import useAPI from '../../../hooks/useAPI';
import styled from 'styled-components';
import { getAccountId } from '../../../selectors/account';
import { getCompanyId } from '../../../selectors/company';
import {Layout} from "@hub-mono/ui";

const InProductReviewPopUp = () => {
    const company = useSelector(({ companyReducer }) => ({
        subdomain: companyReducer.company.subdomain,
        status: companyReducer.company.status,
        plan: companyReducer.company.billing?.planName || 'UNKNOWN',
        companyname: companyReducer.company.name,
        reviews: companyReducer.company.reviews,
    }));

    const user = useSelector(({ account }) => ({
        email: account.email,
        rid: account.resourceId,
        cid: account.companyId,
        firstName: account.firstName,
        lastName: account.lastName,
        role: account.resourceRole,
    }));

    const reviewsEnabled = Boolean(company.reviews?.enabled);
    const roleEnabledForReviews = company.reviews?.enabledForRoles?.includes(user.role) ?? false;

    const { data: userRating, loading } = useAPI(`/reviews/${user.rid}`, reviewsEnabled && roleEnabledForReviews);

    if (!reviewsEnabled || loading) {
        return null;
    }

    const submitRating = async review => {
        await createReview(review);
    };

    const updateRating = async review => {
        await updateReview(review);
    };

    const contactCustomerSupport = async supportMessage => {
        try {
            await sendContact({
                message: supportMessage,
                subject: 'Contact Support via In-Product Review',
                sender: user.email,
                resource_firstName: user.firstName,
                resource_lastName: user.lastName,
                resource_email: user.email,
                resource_company: company.companyname,
                resource_role: user.role,
                domain: company.subdomain,
            });
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <InProductReview
            company={company}
            user={user}
            userRating={userRating}
            onContactSupport={contactCustomerSupport}
            submitRating={submitRating}
            updateRating={updateRating}
        />
    );
};

const SchedulerTemplate = props => {
    const { component: Component, isFullScreen, ...restOfProps } = props;
    const companyStatus = useSelector(state => state.companyReducer?.company?.status);
    const resourceId = useSelector(getAccountId);
    const companyId = useSelector(getCompanyId);

    const { openHubSpotChat } = useHubSpotChat();

    useEffect(() => {
        openHubSpotChat();
    }, [openHubSpotChat, companyStatus]);

    return (
        <Route
            {...restOfProps}
            render={matchProps => (
                <div className={restOfProps.location.pathname.split('/')[1]} style={{ height: 'inherit' }}>
                    <Header />
                    <div className="container-fluid" style={{ height: 'inherit' }}>
                        <Row style={{ height: 'inherit' }}>
                            <div
                                className={classNames('full-screen', {
                                    on: isFullScreen,
                                    'off col-sm-3 col-md-4 col-lg-3 col-xl-2': !isFullScreen,
                                })}
                            >
                                <Sidebar rowHeight={69} />
                            </div>
                            <div
                                className={classNames('px-0 scheduler-background', {
                                    col: isFullScreen,
                                    'col-sm-9 col-md-8 col-lg-9 col-xl-10': !isFullScreen,
                                })}
                            >
                                <div className="main scheduler overflow-hidden">
                                    {Component && <Component {...matchProps} />}
                                    {restOfProps.child && (
                                        <Switch>
                                            {restOfProps.child.map(route => (
                                                <RouteValidator key={route.path} {...route} />
                                            ))}
                                        </Switch>
                                    )}
                                </div>
                            </div>
                        </Row>
                    </div>
                    <TipsManager />
                    <PopupsLayout gap="--Spacing-100" hAlign="flex-end">
                        <InProductReviewPopUp />
                        <SurveyPopup accountId={resourceId} companyId={companyId} />
                    </PopupsLayout>
                </div>
            )}
        />
    );
};

const PopupsLayout = styled(Layout)`
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 1000 !important;
    margin-bottom: 64px;
    margin-right: 86px;
`

SchedulerTemplate.propTypes = {
    component: PropTypes.oneOfType([PropTypes.func, PropTypes.element, PropTypes.object]),
    path: PropTypes.string.isRequired,
    paramValidator: PropTypes.func,
};

SchedulerTemplate.defaultProps = {
    paramValidator: null,
};

export default SchedulerTemplate;
